import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkles, ArrowRight, UserPlus2, CheckCheck } from "lucide-react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const iconFor = (type) => {
  if (type === "created") return Sparkles;
  if (type === "assigned") return UserPlus2;
  return ArrowRight;
};

const timeAgo = (iso) => {
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const nav = useNavigate();

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 30_000,
  });

  const markMut = useMutation({
    mutationFn: async () => (await api.post("/notifications/mark-read")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unread = data?.unread_count ?? 0;
  const items = data?.items ?? [];

  // Auto mark-read after popover has been open for 800ms
  useEffect(() => {
    if (!open || unread === 0) return;
    const t = setTimeout(() => markMut.mutate(), 800);
    return () => clearTimeout(t);
  }, [open, unread]);

  const goToLead = (leadId) => {
    setOpen(false);
    nav("/leads");
    // Also drop a session hint so leads page can auto-open, if we add that later.
    sessionStorage.setItem("focus_lead", leadId);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="topbar-notifications"
          aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
          className="relative grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="notif-unread-badge"
              className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-popover">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <div className="text-sm font-bold text-navy">Notifications</div>
            <div className="text-[11px] text-slate-500">
              {unread > 0 ? `${unread} unread` : "You're all caught up"}
            </div>
          </div>
          {unread > 0 && (
            <button
              data-testid="notif-mark-read"
              onClick={() => markMut.mutate()}
              className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-navy hover:bg-slate-50"
            >
              <CheckCheck className="h-3 w-3" /> Mark read
            </button>
          )}
        </div>
        <ul className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <li className="px-4 py-8 text-center text-xs text-slate-400">
              No recent activity yet.
            </li>
          )}
          {items.map((n) => {
            const Icon = iconFor(n.type);
            return (
              <li
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                data-unread={n.unread}
                className={`flex cursor-pointer items-start gap-3 border-b border-slate-50 px-4 py-3 transition hover:bg-slate-50 ${
                  n.unread ? "bg-amber-50/40" : ""
                }`}
                onClick={() => goToLead(n.lead_id)}
              >
                <div
                  className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${
                    n.type === "created"
                      ? "bg-emerald-100 text-emerald-600"
                      : n.type === "assigned"
                        ? "bg-blue-100 text-blue-600"
                        : "bg-amber-100 text-amber-600"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-slate-800">
                    {n.message}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {n.user_name ? `by ${n.user_name} · ` : ""}
                    {timeAgo(n.created_at)}
                  </div>
                </div>
                {n.unread && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-rose-500" />}
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
